import type { TourEvent } from '@/features/events';
import { formatDateRangeHero, machineDate } from '@/features/events';
import { Brandmark, DisplayTitle, Eyebrow } from '@/components/typography';
import { ResponsiveImage } from '@/components/media';
import { GoldLine } from '@/components/ornaments';
import { cx } from '@/lib/cx';
import { mexicoMedia } from '../media';
import { Seal2040 } from '../ornaments/Seal2040';
import { HeroWaveEdge } from '../ornaments/HeroWaveEdge';
import styles from './HeroMexico.module.css';
import { HeroReservaCta } from '@/pages/shared/ConversionBlocks';
import { VslInline } from '@/pages/shared/VslInline';
import { MigaGira } from '@/pages/shared/MigaGira';

export const HERO_TITLE_ID = 'mexico-hero-titulo';

/**
 * Reparto de palabras por línea del titular, medido en el mockup:
 * "Linaje," / "Épica y" / "Fuerza" / "Ancestral" (1-2-1-1 palabras).
 * El TEXTO sale de `event.titleMain`; aquí solo vive el RITMO de la maqueta.
 */
const TITLE_LINE_GROUPS = [1, 2, 1, 1] as const;
/** La segunda línea es la única en navy; el resto va en oro (mockup §10). */
const NAVY_LINE_INDEX = 1;

function splitTitleLines(title: string, groups: readonly number[]): string[] {
  const words = title.split(' ').filter(Boolean);
  const expected = groups.reduce((total, size) => total + size, 0);
  // Si el dato cambiara y dejara de encajar, se sirve en una línea antes que
  // partir el titular por un sitio arbitrario.
  if (words.length !== expected) return [title];

  const lines: string[] = [];
  let cursor = 0;
  for (const size of groups) {
    lines.push(words.slice(cursor, cursor + size).join(' '));
    cursor += size;
  }
  return lines;
}

export interface HeroMexicoProps {
  event: TourEvent;
}

/**
 * S1 · HERO ANCESTRAL — `DEMO_4_Mexico.png`, banda y 0-660.
 *
 * Composición de tres bandas sobre una sola escena fotográfica: Ángel de la
 * Independencia y bandera a la izquierda, columna tipográfica centrada,
 * Azrahel a la derecha, y el sello "2040" anclado al pie de la banda derecha.
 *
 * El ORDEN DEL DOM es el del móvil (marca → ciudad → fecha → titular → lead →
 * sello → imagen). En escritorio la imagen se posiciona en absoluto DETRÁS del
 * texto: misma etiqueta `<img>`, sin duplicar descargas ni `alt`.
 */
export function HeroMexico({ event }: HeroMexicoProps) {
  const titleLines = splitTitleLines(event.titleMain, TITLE_LINE_GROUPS);

  return (
    <section className={styles.hero} aria-labelledby={HERO_TITLE_ID}>
      {/* Vuelta al hub, visible sin abrir el menú (encargo del cliente,
          25-ago-2026). Va anclada a la celda del hero, no en flujo: este hero se
          come el hueco de la cabecera para que la foto llegue a y=0. */}
      <MigaGira event={event} className={styles.miga} />
      <div className={styles.inner}>
        <div className={styles.brand}>
          {/* El emblema oficial viene de serie en `Brandmark` desde el
              manifiesto (ADR-002): la página ya no lo inyecta. */}
          <Brandmark layout="stacked" size="lg" eager />
        </div>

        <div className={styles.group}>
          <Eyebrow ink="gold" align="center" withSparkles className={styles.city}>
            {event.city}
          </Eyebrow>

          <p className={styles.date}>
            <time dateTime={machineDate(event)}>{formatDateRangeHero(event)}</time>
          </p>
        </div>

        <div className={styles.group}>
          <DisplayTitle id={HERO_TITLE_ID} level={1} ink="gold" align="start" className={styles.title}>
            {titleLines.map((line, index) => (
              <span
                key={line}
                className={cx(styles.titleLine, index === NAVY_LINE_INDEX && styles.titleLineNavy)}
              >
                {line}
              </span>
            ))}
          </DisplayTitle>
        </div>

        <div className={styles.group}>
          {/* EL VSL, ENCIMA DE LA PROMESA. Encargo del ORACLE (12-sep-2026): «debería
              ir arriba del texto rompe el motor». Y va como HERMANO del texto, no
              colgando del botón de compra: ahí dentro heredaba el ancho de un flex
              que se encoge a su contenido y el vídeo salía a 290 px en Buenos Aires
              y 478 en Bogotá —un sello de correos, y distinto en cada ciudad—.
              Aquí coge el ancho del bloque de texto, el del titular. */}
          <VslInline event={event} />

          <GoldLine ornament="diamond" width="md" className={styles.rule} />

          <p className={styles.lead}>
            {event.promiseLines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </p>

          {/* El botón de compra colgando de la promesa (encargo del cliente). */}
          <HeroReservaCta event={event} />
        </div>

        <Seal2040 className={styles.seal} />
      </div>

      <div className={styles.media}>
        <ResponsiveImage
          asset={mexicoMedia.heroLandmark}
          sizes="100vw"
          className={styles.landmark}
          loading="eager"
          fetchPriority="high"
        />

        {/* El velo crema que sostiene el contraste del texto vive AQUÍ, entre la
            fotografía y el retrato, y no como `::before` del bloque de texto.
            INCIDENTE (ORACLE, en navegador): siendo `.inner::before` se pintaba
            POR ENCIMA de `.media` —`.inner` es un contexto de apilado propio— y
            su radial lamía a Azrahel: el cuerpo se disolvía hacia abajo en un
            lavado lechoso. Ahora el retrato va después del velo en el mismo
            grid, así que lo tapa: el cuerpo llega entero al borde y quien lo
            corta es la onda de la sección, como en el mockup. */}
        <span className={styles.veil} aria-hidden="true" />

        <ResponsiveImage
          asset={mexicoMedia.heroPortrait}
          sizes="(min-width: 48rem) 40vw, 60vw"
          className={styles.portrait}
          loading="eager"
        />
      </div>

      <HeroWaveEdge className={styles.edge} />
    </section>
  );
}
